import React from 'react';
import useArticle from 'vl-common/src/hooks/useArticle';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

const StyledArticle = styled.div`
  max-width: 860px;
  margin: 0 auto;

  h1,
  h2 {
    font-size: 16px;
    margin: 1em 0;
  }

  .columnLayout {
    @media (min-width: 768px) {
      display: grid;
      gap: 1em;
    }

    display: flex;
  }

  hr {
    margin: 1em 0;
    border: 1px solid #ccc;
  }

  .two-equal {
    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      margin-bottom: 1em;
    }

    flex-direction: column;
  }

  summary {
    cursor: pointer;
    font-size: 14px;
  }

  details {
    border-bottom: 1px solid #ccc;
    padding: 0.5em 1em;
    margin: 1em 0;
  }

  .expand-content {
    margin-top: 1em;
  }

  p {
    font-size: 14px;
  }
`;

/**
 * Renders an HTML string from Confluence with appropriate styling.
 *
 * **Note:** This component uses `dangerouslySetInnerHTML`, so don’t provide user-generated content.
 */
const ConfluenceArticle: React.FC<{
  id: string;
}> = ({ id }) => {
  const article = useArticle(id) as any;

  const html = article?.body;

  const regexExpanderControl = /<div( id="expander-control-.*?<\/)div>/g;
  const regexExpandContainer = /<div( id="expander-.*?")( class="expand-container .*?<\/)div><\/div>/g;
  const regexAcControl = /<ac:parameter( ac:name=""title"".*?)<\/ac:parameter>/g;
  const regexAcContainer = /<ac:structured-macro( ac:name=""expand"" .*?)<\/ac:structured-macro>/g;
  const regexAcColumns = /<ac:layout-section ac:type=""two_equal""(.*?)<\/ac:layout-section>/g;

  const htmlWithDetails = html
    ?.replace(regexExpanderControl, '<summary$1summary>')
    ?.replace(regexExpandContainer, '<details$2div></details>')
    ?.replace(regexAcControl, '<summary$1</summary>')
    ?.replace(regexAcContainer, '<details$1</details>')
    ?.replace(regexAcColumns, '<div class="columnLayout two-equal" data-layout="two-equal"$1</div>');

  const title = article?.title;

  return (
    <>
      {title && (
        <h1
          css={css`
            font-size: 1.5em;
            font-weight: 600;
            padding: 1.5em 0;
            text-align: center;
            max-width: 860px;
            margin: 0 auto;
          `}
        >
          {title}
        </h1>
      )}
      <StyledArticle dangerouslySetInnerHTML={{ __html: htmlWithDetails }} />
    </>
  );
};

export default ConfluenceArticle;
